import { Input, Space, Switch, Table, Typography, notification, Skeleton } from "antd"
import { useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useInfiniteQuery } from "react-query"
import { useDebouncedState } from "../../hooks/useDebouncedState"
import { getJobList } from "./jobSelectorModel"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons"

const { Text } = Typography

export type Props = {
  onJobSelected: (jobId: number, jobName: string) => void
}

const JobSelector = ({ onJobSelected }: Props) => {
  const [openJobsOnly, setOpenJobsOnly] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const { debouncedState: debouncedSearchQuery, setState: setDebouncedSearchQuery } = useDebouncedState('', 250)
  const jobListQuery = useInfiniteQuery(['jobList', { openJobsOnly, debouncedSearchQuery }],
    ({ pageParam = 1 }) => {
      return getJobList({
        openJobsOnly,
        searchTerm: debouncedSearchQuery,
        page: pageParam,
        activeJobsOnly: true,
        soldJobsOnly: true
      })
    },
    {
      getNextPageParam: (_, pages) => pages.length + 1,
      staleTime: 60000,
      refetchOnWindowFocus: false,
      onError: () => {
        notification.error({
          message: 'There was a problem loading the job list. Please try again after a few moments.'
        })
      }
    }
  )

  const numberOfItemsRequiredToScroll = 7
  const jobList = jobListQuery.data?.pages.flatMap(x => x) ?? []
  const hasMore = !jobListQuery.data?.pages.some(x => x.length === 0) && jobList.length > numberOfItemsRequiredToScroll

  return <Space direction="vertical">
    <Space.Compact>
      <Space.Addon><FontAwesomeIcon icon={faMagnifyingGlass} /></Space.Addon>
      <Input
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value)
          setDebouncedSearchQuery(e.target.value)
        }}
      />
      <Space.Addon>
        <Space>
          <Switch checked={openJobsOnly} onChange={(x) => {
            setOpenJobsOnly(x)
          }} />
          <span style={{ whiteSpace: 'nowrap' }}>Open Jobs Only</span>
        </Space>
      </Space.Addon>
    </Space.Compact>
    <InfiniteScroll
      style={{ border: '1px solid lightgray' }}
      height={400}
      dataLength={jobList.length}
      hasMore={hasMore}
      next={() => jobListQuery.fetchNextPage()}
      loader={<Skeleton active paragraph={{ rows: 1 }} />}
    >
      <Table
        loading={jobListQuery.isLoading}
        dataSource={jobList}
        rowKey={(x) => x.jobId}
        showHeader={false}
        columns={[
          {
            render: (_, item) => {
              const jobNo = item.jobNo ? <Text type="secondary">#{item.jobNo}</Text> : <></>
              const where = item.propertyNickname ? item.propertyNickname : item.propertyAddress1
              const customerLine = where ? `${item.customerName} at ${where}` : item.customerName
              return <div style={{ cursor: "pointer" }}>
                {jobNo} <Text>{item.jobName}</Text><br />
                <Text type="secondary">{customerLine}</Text>
              </div>
            }
          }
        ]}
        onRow={(x) => ({
          onClick: () => onJobSelected(x.jobId, x.jobName)
        })}
        pagination={false}
        size="small"
      />
    </InfiniteScroll>
  </Space>
}
export default JobSelector
